import React, {useCallback, useMemo, useState} from "react";
import "./index.scss";
import {checkIfItsUrl, generateFullUrl, useMediaQuery} from "services/utils/utils";
import {
    ACHIEVEMENT_LEVELS, COMPANY_ADDRESS_TYPES,
    COMPANY_ROLES,
    EMPLOYEES,
    LANGUAGES,
    MOBILE_MEDIA_QUERY_THRESHOLD,
    REFERENCE_SECTORS
} from "constants.js";
import {get, keyBy} from "lodash";
import Button from "features/components/button";
import {__} from "services/i18n";
import Title from "features/components/title";
import Text from "features/components/text";
import {Icon} from "features/components/icon";
import moment, {ISO_8601} from "moment";
import {CategoryTagBox} from "features/components/category-tag-box";
import ReactPlayer from "react-player";
import {Tabber, TabberChild} from "features/components/tabber";
import {connect} from "react-redux";
import {staticDataOsaOffices} from "store/reducer/static/selectors";
import {skillConfirmLoadingSelector} from "store/reducer/skills/confirm/selectors";
import {skillConfirmStart} from "store/reducer/skills/confirm/reducer";
import {LoadingLogoPortal} from "features/components/loading-logo";
import {RecommendationItem} from "features/pages/profile/recommendation/item";

/* istanbul ignore next */
const mapStateToProps = state => ({
    osa_offices: staticDataOsaOffices(state),
    skill_loading: skillConfirmLoadingSelector(state)
});
/* istanbul ignore next */
const mapDispatchToProps = {
    skillConfirmStart
};

export const UserProfile = (props) => {

    const {data, osa_offices, openOnCompanyId, enableConfirmation = false, skill_loading, skillConfirmStart} = props;

    const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY_THRESHOLD);

    const [showContacts, setShowContacts] = useState(false);
    const renderBackdrop = useCallback(() => {
        if(!isMobile) return null;
        return <div className="backdrop" onClick={() => setShowContacts(false)} />
    }, [isMobile]);


    const renderSkills = useCallback((skills, id, type) => {
        return <div className="skills">
            {skills.map(i => <div key={i.skill_name} className="skill">
                <Title size={12}>{i.skill_name}</Title>
                <div className={"confirm-number"}>
                    <Icon>thumbs-up</Icon>
                    <Text size={14}>{__(`public-profile.skills.${i.confirmers_id.length === 0 ? "not-confirmed" : "confirmed-by"}`, [{key: "[number]", value: i.confirmers_id.length}])}</Text>
                </div>
                {enableConfirmation && <Button size="small" onClick={() => skillConfirmStart({skill_name: i.skill_name, id: id, type: type})} title={__(`buttons.${i.confirmed_by_you ? "you-confirmed" : "confirm"}`)} disabled={i.confirmed_by_you} />}
            </div>)}
        </div>
    }, [enableConfirmation, skillConfirmStart]);

    const renderRecommendations = useMemo(() => {
        const recommendations = data.recommendations;
        if(!data || recommendations.length === 0) return null;
        return <>
            <span className="separator" />
            {recommendations.map(i => <RecommendationItem content={i.content} name={i.adviser.name} avatar={i.adviser.avatar} extra={moment(i.created_at, ISO_8601).format("MMMM YYYY")} /> )}
        </>
    }, [data]);

    const renderProfile = useCallback(() => {
        const profile = data.profile;
        const achievements = data.achievements;
        const videos = data.videos.map(i => ({title: i.title, tags: i.tags, categories: i.categories, role: keyBy(i.speakers, "id")[profile.id].title}));
        return <div className="profile">
            <div className="left">
                {(isMobile && (profile.email || profile.phone || profile.linkedin || profile.twitter || profile.website || (profile.links && profile.links.filter(i => i.link !== null).length > 0)))
                && <div className="mobile-contact"><Button title={__("buttons.contact-name", [{key: "[name]", value: profile.firstName}])} onClick={() => setShowContacts(true)} type={"accent"} icon={"arrow-circle-right"} /></div>}
                {profile.skills && <><div className="section">
                    <Title size={20}>{__("public-profile.profile.skills")}</Title>
                    {renderSkills(profile.skills_raw, profile.id, "user")}
                </div>
                    <span className="separator" />
                </>}
                {profile.userBio && <><div className="section">
                    <Title size={20}>{__("public-profile.profile.description")}</Title>
                    <Text size={16}>{profile.userBio}</Text>
                </div>
                    <span className="separator" />
                </>}
                <div className="section created-langs">
                    <div className="">
                        <Icon>osa</Icon>
                        <div>
                            <Text size={14}>{__("public-profile.profile.from", [{key: "[date]", value: moment(profile.created_at, ISO_8601).format("DD MMMM YYYY")}])}</Text>
                            <Title size={18}>{get(osa_offices.find(i => i.key === profile.office), 'value', profile.office)}</Title>
                        </div>
                    </div>
                    <div className="">
                        <Icon>mic</Icon>
                        <div>
                            <Text size={14}>{__("public-profile.profile.langs")}</Text>
                            <Title size={18}>{profile.languages && profile.languages.map(i => get(keyBy(LANGUAGES, "key")[i], "value", i)).join(", ")}</Title>
                        </div>
                    </div>
                </div>
                {
                    achievements.length > 0 && <>
                        <span className="separator" />
                        <div className="section achievements">
                            <Title size={20}>{__("public-profile.achievements.title", [{key: "[name]", value: profile.firstName}])}</Title>
                            {achievements.map(item => <div key={item.category} className={`achievement ${item.level}`}>
                                <Title size={16}>{item.category}</Title>
                                <div>
                                    <Icon>{item.level === ACHIEVEMENT_LEVELS.pro ? "star" : item.level === ACHIEVEMENT_LEVELS.expert ? "thumbs-up" : "bookmark"}</Icon>
                                    <div>
                                        <Text size={14}><span className={"level-name"}>{__(`common.achievements.level.${item.level}`)}</span> {__("public-profile.achievements.in-category", [{key: "[cat_name]", value: item.category}])}</Text>
                                        <div className="stats">
                                            <Text size={14}>{__("public-profile.achievements.comments", [{key: "[count]", value: item.details.comments_count}])}</Text>
                                            <Text size={14}>{__("public-profile.achievements.upvotes", [{key: "[count]", value: item.details.upvotes_count}])}</Text>
                                            <Text size={14}>{__("public-profile.achievements.best", [{key: "[count]", value: item.details.best_answers_count}, {key:"[cat_name]", value: item.category}])}</Text>
                                        </div>
                                    </div>
                                </div>
                            </div>)}
                        </div>
                    </>
                }
                {renderRecommendations}
                <span className="separator" />
                {
                    videos.length > 0 && <div className="section">
                        <Title size={20}>{__("public-profile.profile.videos", [{key: "[name]", value: profile.firstName}])}</Title>
                        {videos.map(i => <div key={i.title} className="video">
                            <Title size={16}>{i.role} {__("common.for")}</Title>
                            <div>
                                <Text size={14}>{i.title}</Text>
                                <CategoryTagBox tags={i.tags} categories={i.categories} />
                            </div>
                        </div>)}
                    </div>
                }
            </div>
            {(!isMobile || showContacts) && <>
                {renderBackdrop()}
                <div className="right">
                    {profile.email && <div className="link" onClick={() => window.open(`mailto:${profile.email}`)}>
                        <Icon>email</Icon>
                        <Text size={16}>{profile.email}</Text>
                    </div>}
                    {profile.phone && <div>
                        <Icon>phone</Icon>
                        <Text size={16}>{profile.phone}</Text>
                    </div>}
                    {profile.linkedin && <div className="link" onClick={() => window.open(checkIfItsUrl(profile.linkedin), "_blank")}>
                        <Icon>linkedin</Icon>
                        <Text size={16}>{__("public-profile.profile.linkedin", [{
                            key: "[name]",
                            value: profile.firstName
                        }])}</Text>
                    </div>}
                    {profile.twitter && <div className="link" onClick={() => window.open(checkIfItsUrl(profile.twitter), "_blank")}>
                        <Icon>twitter</Icon>
                        <Text size={16}>{__("public-profile.profile.twitter", [{
                            key: "[name]",
                            value: profile.firstName
                        }])}</Text>
                    </div>}
                    {profile.website && <div className="link" onClick={() => window.open(checkIfItsUrl(profile.website), "_blank")}>
                        <Icon>web</Icon>
                        <Text size={16}>{__("public-profile.profile.website", [{
                            key: "[name]",
                            value: profile.firstName
                        }])}</Text>
                    </div>}
                    {profile.links && profile.links.filter(i => i.link !== null).map(i => <div className="link" key={i.link}
                                                                                               onClick={() => window.open(checkIfItsUrl(i.link), "_blank")}>
                        <Icon>web</Icon>
                        <Text size={16}>{i.desc}</Text>
                    </div>)}
                </div>
            </>}
        </div>
    }, [data, isMobile, osa_offices, renderBackdrop, renderSkills, showContacts, renderRecommendations]);

    const renderCompany = useCallback((company) => {
        const products = company.products.filter(i => !!i.title);
        return <div className="company">
            <div className="left">
                {isMobile && <div className="mobile-contact"><Button title={__("buttons.contact-name", [{key: "[name]", value: company.name}])} onClick={() => setShowContacts(true)} type={"accent"} icon={"arrow-circle-right"} /></div>}
                <div className="logo-name">
                    <div>
                        {company.logo && <img src={generateFullUrl(company.logo)} alt={"company logo"} />}
                        <div>
                            <Title size={20}>{company.name}</Title>
                            <Text size={14}>{__("public-profile.company.vat")} {company.vat}</Text>
                        </div>
                    </div>
                    {company.document && <Button title={__("public-profile.company.presentation")}
                                                 onClick={() => window.open(generateFullUrl(company.document), "_blank")} type={"outlined"}
                                                 icon={"download"}/>}
                </div>
                {
                    company.video && <div className="company-video"><ReactPlayer controls={true} width={"100%"} height={"100%"} url={company.video} /></div>
                }
                <span className="separator" />
                {((company.skills && company.skills.length > 0) || !!company.description) && <>
                    <div>
                        <Title size={20}>{__("public-profile.company.sector")}</Title>
                        {company.skills && renderSkills(company.skills_raw, company.id, "company")}
                        <Text size={16}>{company.description}</Text>
                    </div>
                    <span className="separator" />
                </>
                }
                {(company.associates && company.associates.length > 0) && <>
                    <Title size={20}>{__("public-profile.company.associates", [{key: "[name]", value: company.name}])}</Title>
                    <div className="associates">
                        {company.associates.map(i => <div key={i.id} className="associate">
                            {!!i.image ? <img src={generateFullUrl(i.image)} alt="associate profile" className="avatar" /> : <div className="avatar"><Icon>account-circle</Icon></div>}
                            <Title size={16} align={"center"}>{i.firstName} {i.lastName}</Title>
                            <Text size={14} align={"center"}>{__(get(keyBy(COMPANY_ROLES, "key")[i.role], "value", i.role))}</Text>
                        </div>)}
                    </div>
                    <span className="separator" />
                </>}
                <div className="section created-langs">
                    <div>
                        <Icon>osa</Icon>
                        <div>
                            <Text size={14}>{__("public-profile.company.from", [{key: "[date]", value: moment(company.created_at, ISO_8601).format("DD MMMM YYYY")}])}</Text>
                            <Title size={18}>{get(osa_offices.find(i => i.key === company.osaCenter), 'value', company.osaCenter)}</Title>
                        </div>
                    </div>
                    <div>
                        <Icon>calendar</Icon>
                        <div>
                            <Text size={14}>{__("public-profile.company.year")}</Text>
                            <Title size={18}>{company.creationYear}</Title>
                        </div>
                    </div>
                    <div>
                        <Icon>user-add</Icon>
                        <div>
                            <Text size={14}>{__("public-profile.company.dimension")}</Text>
                            <Title size={18}>{__(get(keyBy(EMPLOYEES, "key")[company.employees], "value", company.employees))}</Title>
                        </div>
                    </div>
                    <div>
                        <Icon>tag</Icon>
                        <div>
                            <Text size={14}>{__("public-profile.company.reference-sector")}</Text>
                            <Title size={18}>{__(get(keyBy(REFERENCE_SECTORS, "key")[company.referenceSector], "value", company.referenceSector))}</Title>
                        </div>
                    </div>
                </div>
                <span className="separator" />
                {products.length > 0 && <div>
                    <Title size={20}>{__("public-profile.company.products")}</Title>
                    {products.map(i => <div className="product">
                        <Title size={16}>{i.title}</Title>
                        <Text size={14}>{i.description}</Text>
                    </div>)}
                </div>}
            </div>
            {(!isMobile || showContacts) && <>
                {renderBackdrop()}
                <div className="right">
                    {company.email && <div className="link" onClick={() => window.open(`mailto:${company.email}`)}>
                        <Icon>email</Icon>
                        <Text size={16}>{company.email}</Text>
                    </div>}
                    {company.phone && <div>
                        <Icon>phone</Icon>
                        <Text size={16}>{company.phone}</Text>
                    </div>}
                    {company.linkedin && <div className="link" onClick={() => window.open(checkIfItsUrl(company.linkedin), "_blank")}>
                        <Icon>linkedin</Icon>
                        <Text size={16}>{__("public-profile.company.linkedin")}</Text>
                    </div>}
                    {company.twitter && <div className="link" onClick={() => window.open(checkIfItsUrl(company.twitter), "_blank")}>
                        <Icon>twitter</Icon>
                        <Text size={16}>{__("public-profile.company.twitter")}</Text>
                    </div>}
                    {company.website && <div className="link" onClick={() => window.open(checkIfItsUrl(company.website), "_blank")}>
                        <Icon>web</Icon>
                        <Text size={16}>{__("public-profile.company.website")}</Text>
                    </div>}
                    {company.address && company.address.map(i => <div key={i.address}>
                        <Icon>location</Icon>
                        <Text size={16}>{i.province} ({get(keyBy(COMPANY_ADDRESS_TYPES, "key")[i.type], "value", i.type)})</Text>
                    </div>)}
                </div>
            </>}
        </div>
    }, [isMobile, osa_offices, showContacts, renderBackdrop, renderSkills]);

    const tabberBaseIndex = useMemo(() => {
        if(openOnCompanyId) {
            return data.companies.findIndex(c => c.id === parseInt(openOnCompanyId)) + 1;
        }
        return 0;
    }, [openOnCompanyId, data])

    return <div className="user-profile">
        {skill_loading && <LoadingLogoPortal />}
        <Tabber disableTab={false} base_index={tabberBaseIndex}>
            <TabberChild title={data.profile.firstName}>
                {renderProfile()}
            </TabberChild>
            {data.companies.map(i => <TabberChild key={i.id} title={i.name}>
                {renderCompany(i)}
            </TabberChild>)}
        </Tabber>
    </div>
}
export default connect(mapStateToProps, mapDispatchToProps)(UserProfile);